import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { PhoneOff } from 'lucide-react';
import { HostSettingsCard, HostToggleRow } from './HostSettingsCard';
import { fetchAccountSettings, updateAccountPrivacy } from '../utils/accountSettingsApi';

/**
 * "Allow phone calls" privacy card — shown only in guest and host settings.
 *
 * When a user turns this OFF:
 *  • their partner (host↔guest) stops seeing the phone button in the chat
 *  • the backend rejects any new /calls/initiate for or from this user
 * See backend routes/call_routes.py::initiate_call.
 *
 * Venues and venue members intentionally NEVER see this toggle — voice
 * calling is not offered on the venue side of the app.
 */
export default function AllowPhoneCallsCard({ dataTestId = 'allow-phone-calls-card' }) {
  const [allowCalls, setAllowCalls] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchAccountSettings()
      .then((settings) => {
        setAllowCalls(settings.privacy?.allow_phone_calls !== false);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const handleToggle = async () => {
    const next = !allowCalls;
    setAllowCalls(next); // optimistic
    try {
      await updateAccountPrivacy({ allow_phone_calls: next });
      toast.success(next ? 'Phone calls are back on' : 'Phone calls turned off');
    } catch (err) {
      setAllowCalls(!next); // rollback
      toast.error(err.response?.data?.detail || 'Failed to update phone-call preference');
    }
  };

  if (!loaded) return null;

  return (
    <HostSettingsCard data-testid={dataTestId}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <PhoneOff size={24} color="var(--text)" />
        <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--text)', margin: 0 }}>Voice calls</p>
      </div>

      <HostToggleRow
        label="Allow phone calls"
        description="Let hosts and guests you've matched with call you inside the app. Turn off to hide the phone button from your side and theirs."
        checked={allowCalls}
        onChange={handleToggle}
        dataTestId={`${dataTestId}-toggle`}
      />
    </HostSettingsCard>
  );
}
